/**
 * /trust — public security & data trust page. No auth required.
 *
 * Designed to be sent to enterprise HR prospects BEFORE a demo so the
 * "how do you handle our data?" conversation is already answered.
 *
 * Submitting the certification-request form posts to
 * /api/trust/security-letter-request which emails tejinder.sehdev@roleiq.pro.
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ShieldCheck, Activity, Lock, Layers, ScrollText, Database, Globe, ArrowRight, CheckCircle2 } from "lucide-react";
import usePageTitle from "@/hooks/usePageTitle";
import { LogoDark } from "@/components/brand/Logo";

const API = process.env.REACT_APP_BACKEND_URL;
const CERT_OPTIONS = ["SOC 2", "ISO 27001", "DPDP", "Other"];

function PostureCard({ icon: Icon, title, body }) {
  return (
    <div className="bg-white rounded-2xl border border-navy/10 p-6 hover:border-gold transition-colors" data-testid={`posture-card-${title.split(" ")[0].toLowerCase()}`}>
      <div className="flex items-center gap-2 mb-3">
        <div className="p-2 rounded-lg bg-gold/10"><Icon className="w-5 h-5 text-gold" /></div>
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-green-50 text-green-700 font-dm text-[10px] font-bold uppercase tracking-wide">
          <CheckCircle2 className="w-3 h-3" /> Active
        </span>
      </div>
      <h3 className="font-playfair text-navy text-lg font-bold mb-2">{title}</h3>
      <p className="font-dm text-navy/70 text-sm leading-relaxed">{body}</p>
    </div>
  );
}

function Section({ icon: Icon, title, children, testid }) {
  return (
    <section className="bg-white rounded-2xl border border-navy/10 p-7 mb-6" data-testid={testid}>
      <div className="flex items-center gap-3 mb-4">
        <Icon className="w-6 h-6 text-gold" />
        <h2 className="font-playfair text-navy text-2xl font-bold">{title}</h2>
      </div>
      <div className="font-dm text-navy/80 leading-relaxed space-y-3">{children}</div>
    </section>
  );
}

export default function TrustPage() {
  usePageTitle("Security & Data Trust");
  const [form, setForm] = useState({ name: "", company: "", email: "", certification: "DPDP", message: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.name.trim() || !form.company.trim() || !form.email.trim()) {
      setError("Name, company, and email are required."); return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/api/trust/security-letter-request`, form);
      setDone(true);
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not send your request. Try again or email tejinder.sehdev@roleiq.pro directly.");
    } finally { setBusy(false); }
  };

  return (
    <div className="bg-cream min-h-screen" data-testid="trust-page">
      {/* Navy hero */}
      <div className="bg-navy text-cream">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <Link to="/" className="inline-flex items-center"><LogoDark width={140} height={40} /></Link>
          <Link to="/" className="font-dm text-cream/70 text-sm hover:text-gold">← Home</Link>
        </div>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 pt-12 pb-16">
          <div className="flex items-center gap-3 mb-4">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-green-500/15 border border-green-400/30 text-green-300 font-dm text-xs font-bold uppercase tracking-wider"
              data-testid="dpdp-badge">
              <ShieldCheck className="w-3.5 h-3.5" /> DPDP Act 2023 Compliant
            </span>
          </div>
          <h1 className="font-playfair text-4xl sm:text-5xl lg:text-6xl font-bold mb-4">RoleIQ Security &amp; Data Trust</h1>
          <p className="font-dm text-cream/80 text-lg max-w-2xl">
            Built for enterprise HR teams. Your employee data is protected by design, not afterthought.
          </p>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 -mt-8 pb-20">
        {/* Posture summary */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8" data-testid="posture-summary">
          <PostureCard icon={Activity} title="Rate limiting on all endpoints"
            body="Authentication, AI coaching, CapScan generation, TTS, and movement reports all enforce per-IP and per-user request budgets. Abusive bursts are blocked before they reach our compute layer." />
          <PostureCard icon={Lock} title="Prompt injection hardening on all AI surfaces"
            body="Every free-text input that touches an LLM is wrapped in safe delimiters with a security guard instruction. Suspicious patterns are logged to an admin audit feed." />
          <PostureCard icon={Layers} title="Org-level data isolation"
            body="Every database read is scoped by user_id and (where present) org_id. Two colleagues at different companies can never see each other's data — enforced at the query layer." />
          <PostureCard icon={ScrollText} title="DPDP Act 2023 retention compliance"
            body="Automatic TTL deletion of coaching transcripts (24 months) and assessment data (36 months). One-click hard delete from your account settings at any time." />
        </div>

        <Section icon={Database} title="Data retention policy" testid="retention-section">
          <p><strong className="text-navy">Coaching session data:</strong> deleted automatically after 24 months.</p>
          <p><strong className="text-navy">CapScan and assessment reports:</strong> deleted automatically after 36 months.</p>
          <p>You can request immediate deletion of all your data at any time using the <em>Delete My Data</em> option in your account settings. This action is irreversible and includes coaching transcripts, capability reports, movement reports, intelligence dashboards, and your user profile.</p>
        </Section>

        <Section icon={Globe} title="Infrastructure" testid="infra-section">
          <p>Data hosted on MongoDB Atlas, Mumbai region (<code className="bg-cream px-1.5 py-0.5 rounded text-xs">ap-south-1</code>) — keeping Indian customer data inside Indian jurisdiction.</p>
          <p>TLS 1.2+ enforced on every connection. Encryption in transit applies to all traffic between your browser and our servers. Encryption at rest enabled on production-tier (M10+) Atlas clusters.</p>
        </Section>

        <Section icon={ShieldCheck} title="Compliance" testid="compliance-section">
          <p><strong className="text-navy">India Digital Personal Data Protection Act 2023</strong> — compliant. Live audit trail of every prompt-injection attempt, rate-limit rejection, and TTL-driven deletion is maintained internally.</p>
          <p><strong className="text-navy">GDPR-equivalent data deletion rights</strong> — supported. Any user can request hard deletion at any time and receive a deletion receipt of every collection emptied.</p>
        </Section>

        {/* Certification request form */}
        <section className="bg-navy text-cream rounded-2xl p-8" data-testid="cert-form-section">
          <h2 className="font-playfair text-3xl font-bold mb-2">Request our Security &amp; Compliance Letter</h2>
          <p className="font-dm text-cream/70 mb-6 max-w-2xl">
            Procurement team needs more before approval? Tell us which certification you're asking about and we'll respond with our current posture letter.
          </p>
          {done ? (
            <div className="bg-green-500/15 border border-green-400/30 rounded-xl p-6 text-center" data-testid="cert-form-success">
              <CheckCircle2 className="w-10 h-10 text-green-300 mx-auto mb-3" />
              <p className="font-playfair text-2xl font-bold mb-1">Request received</p>
              <p className="font-dm text-cream/80">We'll respond within 2 business days at <strong>{form.email}</strong>.</p>
            </div>
          ) : (
            <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="font-dm text-cream/60 text-xs uppercase tracking-wide block mb-1">Name *</label>
                <input type="text" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                  data-testid="cert-name" className="w-full bg-white/10 border border-cream/20 rounded-lg px-3 py-2.5 font-dm text-cream placeholder-cream/40 focus:border-gold focus:outline-none" />
              </div>
              <div>
                <label className="font-dm text-cream/60 text-xs uppercase tracking-wide block mb-1">Company *</label>
                <input type="text" required value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })}
                  data-testid="cert-company" className="w-full bg-white/10 border border-cream/20 rounded-lg px-3 py-2.5 font-dm text-cream placeholder-cream/40 focus:border-gold focus:outline-none" />
              </div>
              <div>
                <label className="font-dm text-cream/60 text-xs uppercase tracking-wide block mb-1">Email *</label>
                <input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                  data-testid="cert-email" className="w-full bg-white/10 border border-cream/20 rounded-lg px-3 py-2.5 font-dm text-cream placeholder-cream/40 focus:border-gold focus:outline-none" />
              </div>
              <div>
                <label className="font-dm text-cream/60 text-xs uppercase tracking-wide block mb-1">What certification are you looking for?</label>
                <select value={form.certification} onChange={(e) => setForm({ ...form, certification: e.target.value })}
                  data-testid="cert-type" className="w-full bg-white/10 border border-cream/20 rounded-lg px-3 py-2.5 font-dm text-cream focus:border-gold focus:outline-none">
                  {CERT_OPTIONS.map((c) => (<option key={c} value={c} className="text-navy">{c}</option>))}
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="font-dm text-cream/60 text-xs uppercase tracking-wide block mb-1">Anything specific to mention?</label>
                <textarea rows={3} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })}
                  data-testid="cert-message" placeholder="Optional — e.g. industry, data residency requirements, target close date"
                  className="w-full bg-white/10 border border-cream/20 rounded-lg px-3 py-2.5 font-dm text-cream placeholder-cream/40 focus:border-gold focus:outline-none" />
              </div>
              {error && <p className="md:col-span-2 font-dm text-red-300 text-sm" data-testid="cert-form-error">{error}</p>}
              <div className="md:col-span-2 flex items-center justify-between flex-wrap gap-3">
                <p className="font-dm text-cream/40 text-xs">We respond within 2 business days.</p>
                <button type="submit" disabled={busy} data-testid="cert-submit"
                  className="inline-flex items-center gap-2 bg-gold text-navy px-6 py-3 rounded-lg font-dm font-bold hover:bg-gold/90 disabled:opacity-50">
                  {busy ? "Sending…" : "Send request"} <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </form>
          )}
        </section>

        <div className="mt-10 text-center">
          <p className="font-dm text-navy/40 text-xs">
            Page last updated {new Date().toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" })}.
            For urgent security questions, email <a href="mailto:tejinder.sehdev@roleiq.pro" className="text-gold underline">tejinder.sehdev@roleiq.pro</a>.
          </p>
        </div>
      </div>
    </div>
  );
}
